import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'wouter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Activity,
  Building2,
  Beaker,
  History,
  Bot,
  MonitorDot,
  BarChart,
  ShieldCheck,
  Settings,
  LifeBuoy,
  Search,
  ArrowRight,
  FileText,
} from 'lucide-react';

interface CommandItem {
  id: string;
  title: string;
  category: 'screen' | 'bot' | 'strategy' | 'org' | 'portfolio' | 'agent';
  icon?: React.ComponentType<{ className?: string }>;
  href?: string;
  action?: () => void;
  keywords?: string[];
}

const screenItems: CommandItem[] = [
  { id: 'overview', title: 'Overview', category: 'screen', icon: Activity, href: '/overview', keywords: ['dashboard', 'home'] },
  { id: 'connections', title: 'Connections', category: 'screen', icon: Building2, href: '/connections', keywords: ['exchanges', 'connections'] },
  { id: 'strategies', title: 'Strategies', category: 'screen', icon: Beaker, href: '/strategies', keywords: ['trading', 'algorithms'] },
  { id: 'backtesting', title: 'Backtesting', category: 'screen', icon: History, href: '/backtesting', keywords: ['test', 'simulation'] },
  { id: 'agents', title: 'Agents', category: 'screen', icon: Bot, href: '/agents', keywords: ['agents', 'trading agents', 'agent control room', 'automations', 'bots'] },
  { id: 'console', title: 'Console', category: 'screen', icon: MonitorDot, href: '/console', keywords: ['pilot-assist', 'command'] },
  { id: 'monitoring', title: 'Monitoring', category: 'screen', icon: MonitorDot, href: '/monitoring', keywords: ['alerts', 'status'] },
  { id: 'analytics', title: 'Analytics', category: 'screen', icon: BarChart, href: '/reports', keywords: ['reports', 'analysis'] },
  { id: 'tax', title: 'Tax Center', category: 'screen', icon: FileText, href: '/tax', keywords: ['tax', 'ledger', 'export'] },
  { id: 'compliance', title: 'Compliance', category: 'screen', icon: ShieldCheck, href: '/compliance', keywords: ['audit', 'regulations'] },
  { id: 'admin', title: 'Admin', category: 'screen', icon: Settings, href: '/admin', keywords: ['settings', 'configuration'] },
  { id: 'help', title: 'Help', category: 'screen', icon: LifeBuoy, href: '/help', keywords: ['support', 'documentation'] },
];

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bots?: Array<{ id: string; name: string }>;
  strategies?: Array<{ id: string; name: string }>;
  organizations?: Array<{ id: string; name: string }>;
  portfolios?: Array<{ id: string; name: string }>;
  fullScreen?: boolean;
  extraItems?: CommandItem[];
}

export function CommandPalette({
  open,
  onOpenChange,
  bots = [],
  strategies = [],
  organizations = [],
  portfolios = [],
  fullScreen = false,
  extraItems = [],
}: CommandPaletteProps) {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const RECENTS_KEY = 'command-palette-recents';
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(RECENTS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, 8) : [];
    } catch (error) {
      console.error('Failed to parse recent searches', error);
      return [];
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(RECENTS_KEY, JSON.stringify(recentSearches.slice(0, 8)));
    } catch (error) {
      console.error('Failed to persist recent searches', error);
    }
  }, [recentSearches]);

  const addRecentSearch = useCallback((term: string) => {
    const value = term.trim();
    if (!value) return;
    setRecentSearches((prev) => {
      const next = [value, ...prev.filter((item) => item.toLowerCase() !== value.toLowerCase())];
      return next.slice(0, 8);
    });
  }, []);

  // Combine all items
  const allItems = useMemo(() => {
    const items: CommandItem[] = [...screenItems];

    if (extraItems.length) {
      extraItems.forEach((item) => items.push(item));
    }

    // Add trading agents backed by existing bot runtime records.
    bots.forEach(bot => {
      items.push({
        id: `agent-${bot.id}`,
        title: bot.name,
        category: 'agent',
        icon: Bot,
        href: `/agents?id=${bot.id}`,
      });
    });

    // Add strategies
    strategies.forEach(strategy => {
      items.push({
        id: `strategy-${strategy.id}`,
        title: strategy.name,
        category: 'strategy',
        icon: Beaker,
        href: `/strategies?id=${strategy.id}`,
      });
    });

    // Add organizations
    organizations.forEach(org => {
      items.push({
        id: `org-${org.id}`,
        title: org.name,
        category: 'org',
        action: () => {
          // Switch organization context
          console.log('Switching to org:', org.name);
        },
      });
    });

    // Add portfolios
    portfolios.forEach(portfolio => {
      items.push({
        id: `portfolio-${portfolio.id}`,
        title: portfolio.name,
        category: 'portfolio',
        action: () => {
          // Switch portfolio context
          console.log('Switching to portfolio:', portfolio.name);
        },
      });
    });

    return items;
  }, [bots, strategies, organizations, portfolios, extraItems]);

  // Filter items based on search
  const quickActions = useMemo(() => screenItems.slice(0, 6), []);

  const filteredItems = useMemo(() => {
    if (!search) {
      const quickIds = new Set(quickActions.map((item) => item.id));
      return allItems.filter((item) => !quickIds.has(item.id)).slice(0, 10);
    }

    const searchLower = search.toLowerCase();
    return allItems.filter(item => {
      const titleMatch = item.title.toLowerCase().includes(searchLower);
      const keywordMatch = item.keywords?.some(k => k.toLowerCase().includes(searchLower));
      const categoryMatch = item.category.toLowerCase().includes(searchLower);
      return titleMatch || keywordMatch || categoryMatch;
    }).slice(0, 10); // Limit to 10 results
  }, [search, allItems, quickActions]);

  const handleSelectItem = useCallback((item: CommandItem) => {
    if (search.trim()) {
      addRecentSearch(search);
    }
    if (item.href) {
      setLocation(item.href);
      onOpenChange(false);
    } else if (item.action) {
      item.action();
      onOpenChange(false);
    }
  }, [addRecentSearch, onOpenChange, search, setLocation]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredItems.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
        break;
      case 'Enter':
        e.preventDefault();
        const selectedItem = filteredItems[selectedIndex];
        if (selectedItem) {
          handleSelectItem(selectedItem);
        }
        break;
      case 'Escape':
        onOpenChange(false);
        break;
    }
  }, [filteredItems, selectedIndex, setLocation, onOpenChange]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [open]);

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'screen': return 'Screens';
      case 'agent': return 'Agents';
      case 'strategy': return 'Strategies';
      case 'org': return 'Organizations';
      case 'portfolio': return 'Portfolios';
      default: return category;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 max-w-2xl',
          fullScreen && 'max-w-none w-full h-[100dvh] rounded-none sm:rounded-xl sm:max-w-2xl sm:h-[80vh]'
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Command Palette</DialogTitle>
          <DialogDescription>
            Search for screens, agents, strategies, and more
          </DialogDescription>
        </DialogHeader>

        <div className={cn('flex flex-col', fullScreen ? 'h-full' : '')}>
          <div className={cn('flex items-center border-b', fullScreen ? 'px-5 py-4' : 'px-4')}>
            <Search className="w-5 h-5 text-slate-400 mr-3" />
            <Input
              placeholder="Search screens, agents, strategies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="border-0 focus:ring-0 focus-visible:ring-0 text-base py-4 px-0"
              autoFocus
            />
          </div>

          <div className={cn(fullScreen ? 'flex-1 overflow-y-auto p-5' : 'max-h-96 overflow-y-auto p-2')}>
            {!search && recentSearches.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide">
                  Recent searches
                </h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {recentSearches.map((term) => (
                    <button
                      key={term}
                      type="button"
                      className="px-3 py-1.5 text-sm rounded-full border border-slate-200 bg-white hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-red"
                      onClick={() => setSearch(term)}
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!search && (
              <div className={cn(recentSearches.length > 0 ? 'mt-6' : '')}>
                <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide">
                  Quick actions
                </h3>
                <div className="mt-2 space-y-1">
                  {quickActions.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={`quick-${item.id}`}
                        type="button"
                        className="w-full flex items-center justify-between px-3 py-2 rounded-md text-left transition-colors hover:bg-slate-100 focus:bg-slate-100 focus:outline-none"
                        onClick={() => handleSelectItem(item)}
                      >
                        <div className="flex items-center gap-3">
                          {Icon && <Icon className="w-5 h-5 text-slate-400" />}
                          <div>
                            <div className="font-medium">{item.title}</div>
                            <div className="text-xs text-slate-500">{getCategoryLabel(item.category)}</div>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={cn('space-y-1', !search ? 'mt-6' : '')}>
              {filteredItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No results found for "{search}"
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredItems.map((item, index) => {
                    const Icon = item.icon;
                    const isSelected = index === selectedIndex;

                    return (
                      <button
                        key={item.id}
                        onClick={() => handleSelectItem(item)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={cn(
                          'w-full flex items-center justify-between px-3 py-2 rounded-md text-left transition-colors',
                          'hover:bg-slate-100 focus:bg-slate-100 focus:outline-none',
                          isSelected && 'bg-slate-100'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {Icon && <Icon className="w-5 h-5 text-slate-400" />}
                          <div>
                            <div className="font-medium">{item.title}</div>
                            <div className="text-xs text-slate-500">{getCategoryLabel(item.category)}</div>
                          </div>
                        </div>
                        {isSelected && <ArrowRight className="w-4 h-4 text-slate-400" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={cn('border-t px-4 py-2 flex items-center justify-between text-xs text-slate-500', fullScreen ? 'px-5' : '')}>
            <div className="flex gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">↵</kbd>
                Open
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">Esc</kbd>
                Close
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
